import { DashboardCard } from "../../Dashboard/DashboardCard";
import { ClientLineChart } from "../ClientLineChart";
import type { SubscriptionData } from "../ClientLineChart";
import type {
  ClientOverviewResponse,
  ClientOverviewWithSubscriptionsResponse,
} from "../../../types/client.types";
import { Button } from "@/components/ui/button";
import type { UseQueryResult } from "@tanstack/react-query";
import { Spinner } from "@/components/ui/spinner";

interface ClientOverviewProps {
  data: UseQueryResult<ClientOverviewResponse, Error>;
}

export default function ClientOverview({ data }: ClientOverviewProps) {
  const { data: overview, isLoading, error, refetch, isFetching } = data;

  if (isLoading) {
    return (
      <DashboardCard className="w-full gap-5">
        {/* Header skeleton */}
        <div className="space-y-2">
          <div className="h-5 w-24 animate-pulse rounded bg-white/10" />
          <div className="h-3 w-64 animate-pulse rounded bg-white/10" />
        </div>

        {/* Client info skeleton */}
        <div className="flex w-full flex-row justify-between gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex flex-col gap-2">
              <div className="h-3 w-24 animate-pulse rounded bg-white/10" />
              <div className="h-5 w-32 animate-pulse rounded bg-white/10" />
            </div>
          ))}
        </div>

        {/* Chart skeleton */}
        <div className="flex flex-col gap-2">
          <div className="h-3 w-32 animate-pulse rounded bg-white/10" />
          <div className="h-64 w-full animate-pulse rounded bg-white/10" />
        </div>
      </DashboardCard>
    );
  }

  if (error) {
    return (
      <DashboardCard className="w-full gap-5">
        <div className="flex items-center justify-center p-8">
          <p className="text-red-500">
            Error loading client overview. Please try again.
          </p>
        </div>
      </DashboardCard>
    );
  }

  if (!overview) {
    return (
      <DashboardCard className="w-full gap-5">
        <div className="flex items-center justify-center p-8">
          <p className="text-gray-500">No client data found.</p>
        </div>
      </DashboardCard>
    );
  }

  // Type guard to check if this is the new API response format with subscriptions array
  const isOverviewWithSubscriptions = (
    data: unknown,
  ): data is ClientOverviewWithSubscriptionsResponse => {
    return (
      typeof data === "object" &&
      data !== null &&
      "subscriptions" in data &&
      Array.isArray(
        (data as ClientOverviewWithSubscriptionsResponse).subscriptions,
      ) &&
      "aggregated_quota" in data
    );
  };

  const hasSubscriptions = isOverviewWithSubscriptions(overview);

  // Transform subscription data for the new overview table
  const subscriptionData: SubscriptionData[] = [];
  let chartData: { name: string; value: number; percentage: number }[] = [];

  if (hasSubscriptions) {
    // Calculate total usage across all API keys
    const totalUsage = overview.subscriptions.reduce((sum, sub) => {
      return (
        sum + sub.api_keys.reduce((subSum, key) => subSum + key.usage_count, 0)
      );
    }, 0);

    // Group by subscription (one row per subscription with all its API keys)
    overview.subscriptions.forEach((subscription) => {
      // Calculate total usage for this subscription
      const subscriptionUsage = subscription.api_keys.reduce(
        (sum, key) => sum + key.usage_count,
        0,
      );

      // Calculate percentage based on total usage
      const calculatedPercentage =
        totalUsage > 0 ? (subscriptionUsage / totalUsage) * 100 : 0;

      subscriptionData.push({
        plan_name: subscription.plan_name,
        api_key: subscription.api_keys
          .map((key) => key.api_key_preview)
          .join(", "),
        endpoint_path: subscription.entity_types.join(", "),
        usage: subscriptionUsage,
        percentage: calculatedPercentage,
        subscription_start: subscription.subscription_start,
        subscription_end: subscription.subscription_end,
        billing: subscription.billing || undefined,
      });

      // Populate chartData for the progress bar
      chartData.push({
        name: subscription.plan_name,
        value: subscriptionUsage,
        percentage: calculatedPercentage,
      });
    });
  } else {
    // Fallback to old API format
    chartData =
      overview.api_usage?.endpoint_breakdown?.map((endpoint) => ({
        name: endpoint.api_key,
        value: endpoint.usage,
        percentage: endpoint.percentage,
      })) || [];
  }

  console.log(data.data);

  return (
    <DashboardCard className="w-full cursor-default gap-5">
      <div className="space-y-2">
        <div className="flex w-full flex-row items-center justify-between">
          <div className="text-lg leading-5 font-bold -tracking-wide text-white">
            Overview
          </div>

          <Button
            className="gap-2 p-1.5 text-xs leading-normal font-medium tracking-normal text-white hover:bg-white/30 hover:text-white"
            variant="minimal"
            icon
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? (
              <Spinner size="md" className="stroke-primary!" />
            ) : (
              <i className="ri-loop-right-line text-md"></i>
            )}
          </Button>
        </div>

        <div className="text-black-500 text-xs leading-3">
          Overview of client details and account status
        </div>
      </div>

      {/* Client Information */}
      <div className="flex w-full flex-row items-center">
        <div className="flex w-1/2 flex-col">
          <p className="text-black-500 text-xs leading-3 font-medium">NAME</p>
          <h1 className="text-md leading-5 font-bold -tracking-wide">
            {overview.user.first_name} {overview.user.last_name}
          </h1>
        </div>
        <div className="flex w-1/2 flex-col">
          <p className="text-black-500 text-xs leading-3 font-medium">EMAIL</p>
          <h1 className="text-md leading-5 font-bold -tracking-wide">
            {overview.user.email}
          </h1>
        </div>
      </div>

      {/* API Usage Chart */}
      <div className="flex flex-col gap-2">
        <p className="text-black-500 text-xs leading-3 font-medium">
          API USAGE BREAKDOWN
        </p>
        {hasSubscriptions && subscriptionData.length > 0 ? (
          <ClientLineChart
            endpoints={chartData.map((endpoint) => ({
              ...endpoint,
              id: endpoint.name,
            }))}
            subscriptions={subscriptionData}
            showTable={true}
            isOverview={true}
            quotaTotal={overview.aggregated_quota.quota_total}
            quotaUsed={overview.aggregated_quota.quota_used}
          />
        ) : chartData.length > 0 ? (
          <ClientLineChart
            endpoints={chartData.map((endpoint) => ({
              ...endpoint,
              id: endpoint.name,
            }))}
            showTable={true}
          />
        ) : (
          <div className="bg-black-950 flex h-32 items-center justify-center rounded-lg text-gray-500">
            <div className="text-center">
              <i className="ri-bar-chart-line mb-2 text-2xl"></i>
              <p className="text-sm">No usage data available</p>
            </div>
          </div>
        )}
      </div>
    </DashboardCard>
  );
}
